import consola from 'consola';

type LogData = Record<string, unknown> | undefined;

const LOCAL_OVERRIDE = process.env.REACT_APP_ENABLE_LOGGER;
const isBrowser =
  typeof window !== 'undefined' && typeof window.location !== 'undefined';

const detectLocalhost = (): boolean => {
  if (!isBrowser) {
    return true;
  }
  const host = window.location.hostname ?? '';
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    host.endsWith('.local')
  );
};

const shouldLog =
  LOCAL_OVERRIDE !== 'false' &&
  (LOCAL_OVERRIDE === 'true' ||
    (process.env.NODE_ENV !== 'production' && detectLocalhost()));

type Category =
  | 'Debug'
  | 'Info'
  | 'UserAction'
  | 'Navigation'
  | 'TabNavigation'
  | 'State'
  | 'API'
  | 'Response'
  | 'Warn'
  | 'Error'
  | 'Gateway'
  | 'Cache'
  | 'Storage';

const CSS = {
  time: 'color:#16a34a;font-weight:600', // green
  level: 'color:#9ca3af', // gray
  byCategory: {
    Debug: 'color:#06b6d4',
    Info: 'color:#2563eb',
    UserAction: 'color:#f59e0b',
    Navigation: 'color:#e5e7eb',
    TabNavigation: 'color:#e5e7eb',
    State: 'color:#22d3ee',
    API: 'color:#22c55e',
    Response: 'color:#e879f9',
    Warn: 'color:#eab308',
    Error: 'color:#ef4444',
    Gateway: 'color:#10b981',
    Cache: 'color:#a855f7',
    Storage: 'color:#fb7185',
  } as Record<Category, string>,
  byMethod: {
    GET: 'color:#60a5fa',
    POST: 'color:#22c55e',
    PUT: 'color:#f59e0b',
    DELETE: 'color:#f87171',
    PATCH: 'color:#14b8a6',
    DEFAULT: 'color:#e5e7eb',
  } as Record<string, string>,
} as const;

function nowStr() {
  return new Date().toLocaleTimeString();
}

function printLine(category: Category, message: string, method?: string) {
  if (!shouldLog) return;
  let spec = '';
  const styles: string[] = [];

  spec += '%c' + nowStr() + ' ';
  styles.push(CSS.time);

  spec += '%c' + category + ' ';
  styles.push(CSS.level);

  if (category === 'API' || category === 'Response') {
    const m = (method || '').toUpperCase();
    const methodStyle = CSS.byMethod[m] || CSS.byMethod.DEFAULT;
    spec += '%c' + m + ' ';
    styles.push(methodStyle);
  }

  spec += '%c' + message;
  styles.push(CSS.byCategory[category]);

  // consola proxies to console.log; %c works in browsers
  // eslint-disable-next-line prefer-spread
  consola.log.apply(consola, [spec, ...styles] as any);
}

function logWithData(category: Category, message: string, data?: LogData) {
  if (!shouldLog) return;
  printLine(category, message);
  if (data && Object.keys(data).length > 0) consola.log(data);
}

export function logGateway(message: string, data?: LogData) {
  logWithData('Gateway', message, data);
}

export type GatewayDirection = 'SENT' | 'RECV';
export interface GatewayMeta {
  from?: 'gateway' | 'dashboard' | 'trainee-app' | 'rt-service' | string;
  to?: 'gateway' | 'dashboard' | 'trainee-app' | 'rt-service' | string;
  ctxId?: string; // connection/session id
  note?: string;
}

export function logGatewaySignal(
  direction: GatewayDirection,
  envelope: any,
  meta?: GatewayMeta,
) {
  if (!shouldLog) return;
  const type = envelope?.type ?? 'unknown';
  const inferredCtx =
    envelope?.payload?.sessionId ||
    envelope?.sessionId ||
    envelope?.payload?.routing?.sessionId ||
    '';
  const ctxId = meta?.ctxId || inferredCtx || '';
  const from = meta?.from || (direction === 'RECV' ? 'gateway' : 'dashboard');
  const to = meta?.to || (direction === 'RECV' ? 'dashboard' : 'gateway');
  const head =
    `${direction} ${type} from=${from} to=${to}` +
    (ctxId ? ` ctxId=${ctxId}` : '');
  printLine('Gateway', head);
  try {
    const pretty =
      typeof envelope === 'string'
        ? envelope
        : JSON.stringify(envelope, null, 2);
    consola.log(pretty);
  } catch {
    consola.log(envelope);
  }
}

export function logDebug(message: string, data?: LogData) {
  logWithData('Debug', message, data);
}

export function logInfo(message: string, data?: LogData) {
  logWithData('Info', message, data);
}

export function logUserAction(message: string, data?: LogData) {
  logWithData('UserAction', message, data);
}

export function logNavigation(message: string, data?: LogData) {
  logWithData('Navigation', message, data);
}

export function logTabNavigation(message: string, data?: LogData) {
  logWithData('TabNavigation', message, data);
}

export function logState(message: string, data?: LogData) {
  logWithData('State', message, data);
}

export function logCache(message: string, data?: LogData) {
  logWithData('Cache', message, data);
}

export function logStorage(message: string, data?: LogData) {
  logWithData('Storage', message, data);
}

export function logWarn(message: string, data?: LogData) {
  logWithData('Warn', message, data);
}

export function logError(message: string, data?: LogData) {
  logWithData('Error', message, data);
}

export function logApiRequest(method: string, url: string, data?: LogData) {
  if (!shouldLog) return;
  printLine('API', url, method);
  if (data && Object.keys(data).length > 0) consola.log(data);
}

export function logApiResponse(
  method: string,
  url: string,
  durationMs?: number,
  data?: LogData,
) {
  if (!shouldLog) return;
  const durationLabel =
    typeof durationMs === 'number' ? ` (${durationMs} ms)` : '';
  printLine('Response', `${url}${durationLabel}`, method);
  if (data && Object.keys(data).length > 0) consola.log(data);
}
